/**
 * TOTP Setup Dialog Component
 * Guides users through setting up Time-based One-Time Password (TOTP) authentication
 * Uses AWS Amplify built-in TOTP functions
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Paper,
} from '@mui/material';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import { AppButton, AppTextField } from '../design-system';
import { QRCodeSVG } from 'qrcode.react';
import {
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
} from 'aws-amplify/auth';
import { useTheme } from '../../context/AppContext';

interface TOTPSetupDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const TOTPSetupDialog: React.FC<TOTPSetupDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { theme } = useTheme();
  const muiTheme = useMuiTheme();
  const qrPaperSx = useMemo(
    () => ({
      backgroundColor: muiTheme.palette.common.white,
      border: '1px solid',
      borderColor:
        theme === 'dark'
          ? alpha(muiTheme.palette.common.white, 0.23)
          : alpha(muiTheme.palette.common.black, 0.23),
      borderRadius: 1,
    }),
    [muiTheme, theme],
  );
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // TOTP setup data
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [secretKey, setSecretKey] = useState<string>('');
  
  // Verification
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  const steps = ['Install App', 'Scan QR Code', 'Verify Setup'];

  // Initialize TOTP setup when dialog opens
  useEffect(() => {
    if (open) {
      initializeTOTP();
    } else {
      // Reset state when dialog closes
      setActiveStep(0);
      setError(null);
      setQrCodeUrl('');
      setSecretKey('');
      setVerificationCode('');
    }
  }, [open]);

  const initializeTOTP = async () => {
    setLoading(true);
    setError(null);

    try {
      const totpSetupDetails = await setUpTOTP();
      console.log('TOTP setup initialized');

      // Get the shared secret
      const sharedSecret = totpSetupDetails.sharedSecret;
      setSecretKey(sharedSecret);

      // Generate QR code URL for authenticator apps
      // Format: otpauth://totp/AWSCognito:username?secret=SHAREDSECRET&issuer=WaferInventory
      const appName = 'CnapseInsight';
      const issuer = 'CnapseInsight';
      
      // Construct the otpauth URL
      const otpauthUrl = totpSetupDetails.getSetupUri(appName, issuer);
      setQrCodeUrl(otpauthUrl.href);

    } catch (err) {
      console.error('Failed to initialize TOTP:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize authenticator setup');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setVerifying(true);
    setError(null);

    try {
      // Verify the TOTP code
      await verifyTOTPSetup({ code: verificationCode });
      console.log('TOTP verified successfully');

      // Set TOTP as preferred MFA method
      await updateMFAPreference({ totp: 'PREFERRED' });
      console.log('TOTP set as preferred MFA');

      // Success!
      onSuccess();
    } catch (err) {
      console.error('Failed to verify TOTP:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Verification failed. Please check your code and try again.'
      );
    } finally {
      setVerifying(false);
    }
  };

  const handleClose = () => {
    if (!loading && !verifying) {
      onClose();
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={2}>
            <Typography variant="body1">
              To use an authenticator app for MFA, you'll need to install one on your mobile device.
            </Typography>
            <Typography variant="h6" gutterBottom>
              Recommended Apps:
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">• Google Authenticator (iOS & Android)</Typography>
              <Typography variant="body2">• Microsoft Authenticator (iOS & Android)</Typography>
              <Typography variant="body2">• Authy (iOS, Android & Desktop)</Typography>
              <Typography variant="body2">• 1Password (with TOTP support)</Typography>
            </Stack>
            <Alert severity="info" sx={{ mt: 2 }}>
              Install any of these apps before proceeding to the next step.
            </Alert>
          </Stack>
        );

      case 1:
        return (
          <Stack spacing={3}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="body1">
                  Scan this QR code with your authenticator app:
                </Typography>
                
                {qrCodeUrl && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      display: 'flex',
                      justifyContent: 'center',
                      ...qrPaperSx,
                    }}
                  >
                    <QRCodeSVG value={qrCodeUrl} size={256} level="M" />
                  </Paper>
                )}

                <Alert severity="info">
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Can't scan the QR code?
                  </Typography>
                  <Typography variant="body2">
                    Manually enter this secret key in your authenticator app:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      mt: 1,
                      p: 1,
                      bgcolor: theme === 'dark' ? 'grey.800' : 'grey.100',
                      color: theme === 'dark' ? 'grey.200' : 'grey.900',
                      borderRadius: 1,
                      wordBreak: 'break-all',
                      border: '1px solid',
                      borderColor: theme === 'dark' ? 'grey.700' : 'grey.300',
                    }}
                  >
                    {secretKey}
                  </Typography>
                </Alert>
              </>
            )}
          </Stack>
        );

      case 2:
        return (
          <Stack spacing={2}>
            <Typography variant="body1">
              Enter the 6-digit verification code from your authenticator app:
            </Typography>
            <AppTextField
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setVerificationCode(value);
              }}
              placeholder="123456"
              fullWidth
              autoFocus
              inputProps={{
                maxLength: 6,
                style: { fontSize: '1.5rem', letterSpacing: '0.5rem', textAlign: 'center' },
              }}
              disabled={verifying}
            />
            <Alert severity="info">
              The code changes every 30 seconds. If it doesn't work, wait for a new code.
            </Alert>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set Up Authenticator App</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box>{getStepContent(activeStep)}</Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <AppButton onClick={handleClose} disabled={loading || verifying}>
          Cancel
        </AppButton>
        {activeStep > 0 && (
          <AppButton onClick={handleBack} disabled={loading || verifying}>
            Back
          </AppButton>
        )}
        {activeStep < steps.length - 1 ? (
          <AppButton
            variant="contained"
            onClick={handleNext}
            disabled={loading || (activeStep === 1 && !qrCodeUrl)}
          >
            Next
          </AppButton>
        ) : (
          <AppButton
            variant="contained"
            onClick={handleVerify}
            disabled={verifying || verificationCode.length !== 6}
            startIcon={verifying ? <CircularProgress size={20} /> : undefined}
          >
            {verifying ? 'Verifying...' : 'Verify & Enable'}
          </AppButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
